"use client";

import React from "react";
import { Calendar, CircleCheck as CheckCircle2, ArrowRight, ShieldCheck, Mail, Phone, MapPin, UserCheck, Clock, Loader } from 'lucide-react';
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const trustFeatures = [{
  title: "Direct Access to Yassine",
  description: "You work directly with me at every stage. No account managers, junior hand-offs, or artificial delays."
}, {
  title: "Tailored SEO Strategy",
  description: "Custom search roadmaps aligned with your revenue goals, market competition, and specific industry needs."
}, {
  title: "Transparent Reporting & Clear Metrics",
  description: "Honest performance metrics, regular practical updates, and actionable recommendations without agency jargon."
}, {
  title: "No Long-Term Lock-In Contracts",
  description: "Proven expertise delivered with practical flexibility, focused on long-term sustainable organic growth."
}];
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-muted/60 border border-border rounded-xl text-center space-y-4">
        <div className="inline-flex items-center justify-center size-14 rounded-full bg-accent/20 text-accent mx-auto">
          <CheckCircle2 className="size-7" />
        </div>
        <h3 className="text-xl font-bold font-serif text-foreground">Consultation Request Received</h3>
        <p className="text-muted-foreground text-sm leading-relaxed max-w-md mx-auto">
          {successMessage || "Thank you for reaching out! I will personally review your website details and get back to you within 24 business hours to confirm our meeting time."}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-border hover:bg-background">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="fullName" rules={{
      required: "Full name is required",
      minLength: {
        value: 2,
        message: "Minimum 2 characters required"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium text-xs uppercase tracking-wider">Your Name</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Sarah Jenkins" className="bg-background border-input px-3.5 py-2" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Work email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium text-xs uppercase tracking-wider">Work Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="sarah@yourcompany.com" className="bg-background border-input px-3.5 py-2" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="websiteUrl" rules={{
      required: "Website URL is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium text-xs uppercase tracking-wider">Website URL</FormLabel>
            <FormControl>
              <Input type="text" placeholder="https://yourcompany.com" className="bg-background border-input px-3.5 py-2" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="seoGoal" rules={{
      required: "Please briefly share your main SEO objective"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium text-xs uppercase tracking-wider">Primary SEO Focus or Questions</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Lead generation, technical audit, international rankings..." className="bg-background border-input px-3.5 py-2" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-accent text-accent-foreground hover:bg-accent/90 shadow-md font-semibold text-base py-3.5 h-auto transition-[background-color,box-shadow] cursor-pointer mt-2">
        {isSubmitting ? <>
            <Loader className="animate-spin size-5 mr-2" />
            <span>Processing Request...</span>
          </> : <>
            <span>Book a Free SEO Consultation</span>
            <ArrowRight className="size-5 ml-1" />
          </>}
      </Button>

      <div className="pt-2 text-center text-xs text-muted-foreground space-y-1">
        <p className="flex items-center justify-center gap-1.5">
          <ShieldCheck className="size-4 text-primary" />
          <span>100% Free • Direct strategy discussion • No high-pressure sales</span>
        </p>
      </div>
    </Form>;
}
export default function Consultation() {
  return <section id="consultationcta" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border relative overflow-hidden">
      {}
      <div className="absolute -top-32 -right-32 size-96 bg-accent/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 size-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          
          {}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-7 space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold tracking-wide uppercase">
                <UserCheck className="size-4 text-primary" />
                <span>Direct Consultant Access</span>
              </div>
              
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight leading-tight">
                Ready to Grow Your Search Visibility with a Proven Strategy?
              </h2>

              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed max-w-2xl">
                When you request a consultation, you speak directly with me—Yassine Essaadi. I personally analyze your website opportunity, identify high-impact keyword paths, and outline a realistic SEO roadmap for your business.
              </p>
            </div>

            {}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              {trustFeatures.map((feature, i) => <div key={feature.title} data-index={i} className="p-4 rounded-xl bg-card border border-border space-y-1.5">
                  <div className="flex items-center gap-2 font-semibold text-foreground text-sm font-serif">
                    <CheckCircle2 className="size-4 text-accent shrink-0" />
                    <span>{feature.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-normal pl-6">
                    {feature.description}
                  </p>
                </div>)}
            </div>

            {}
            <div className="pt-4 border-t border-border flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Mail className="size-4 text-primary" />
                <a href="mailto:contact@ye-consultancy.com" className="hover:text-foreground transition-colors underline-offset-4 hover:underline">
                  contact@ye-consultancy.com
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="size-4 text-primary" />
                <a href="tel:+447490984430" className="hover:text-foreground transition-colors underline-offset-4 hover:underline">
                  +44 7490984430
                </a>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="size-4 text-primary" />
                <span>Swindon, UK</span>
              </div>
            </div>

            <div className="pt-2">
              <Button asChild variant="outline" className="border-border hover:bg-muted text-foreground">
                <Link to="/ContactPage">
                  <span>Visit Full Contact Page</span>
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
            </div>
          </motion.div>

          {}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-5">
            <Card className="shadow-lg border-border bg-card">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-xs font-semibold text-primary uppercase tracking-wider flex items-center gap-1">
                    <Clock className="size-3.5" />
                    <span>24-Hour Direct Response</span>
                  </span>
                  <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-md">
                    No Agency Middlemen
                  </span>
                </div>
                <CardTitle className="text-2xl font-bold font-serif text-foreground">
                  Book a Free SEO Consultation
                </CardTitle>
                <CardDescription className="text-muted-foreground text-sm">
                  Fill in your details below to schedule a 1-on-1 strategy call with Yassine.
                </CardDescription>
              </CardHeader>

              <CardContent>
                <FormProvider formId="consultation-cta-form" sectionName="ConsultationCTA" defaultValues={{
                fullName: "",
                email: "",
                websiteUrl: "",
                seoGoal: ""
              }} validationRules={{
                fullName: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                websiteUrl: {
                  required: true
                },
                seoGoal: {
                  required: true
                }
              }} submitText="Book a Free SEO Consultation" successMessage="Thank you! I've received your consultation request and will respond within 24 hours.">
                  <ConsultationForm />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>

        </div>
      </div>
    </section>;
}